// Tutorial sub-flow inside the onboarding "demo" step. Walks the user
// through the core loop: match → chat → look up → save → review. Each
// sub-slide is its own file under demos/ to keep this orchestrator small.
//
// A small text "Back" link in the top-left lets users step back between
// sub-demos without unwinding the whole tutorial via the outer back arrow.

import { useState } from 'react';
import { C } from '@/styles/tokens';
import { MatchDemo } from './demos/MatchDemo';
import { RevealDemo } from './demos/RevealDemo';
import { LookupDemo } from './demos/LookupDemo';
import { SaveDemo } from './demos/SaveDemo';
import { FlashcardDemo } from './demos/FlashcardDemo';

interface DemoStepProps {
  onContinue: () => void;
}

const SUBSTEPS = ['match', 'reveal', 'lookup', 'save', 'flashcard'] as const;
type SubStep = (typeof SUBSTEPS)[number];

export function DemoStep({ onContinue }: DemoStepProps) {
  const [sub, setSub] = useState<SubStep>('match');
  const subIndex = SUBSTEPS.indexOf(sub);

  function advance() {
    const next = SUBSTEPS[subIndex + 1];
    if (next) setSub(next);
    else onContinue();
  }
  function back() {
    const prev = SUBSTEPS[subIndex - 1];
    if (prev) setSub(prev);
  }

  return (
    <div className="flex-1 flex flex-col">
      {subIndex > 0 && (
        <button
          type="button"
          onClick={back}
          className="self-start -ml-1 mb-2 px-1 py-1 ff-body text-[11px] underline underline-offset-4 decoration-1"
          style={{ background: 'transparent', color: C.inkSofter }}
          aria-label="Previous tutorial step"
        >
          Back
        </button>
      )}

      {sub === 'match' && <MatchDemo onContinue={advance} />}
      {sub === 'reveal' && <RevealDemo onContinue={advance} />}
      {sub === 'lookup' && <LookupDemo onContinue={advance} />}
      {sub === 'save' && <SaveDemo onContinue={advance} />}
      {sub === 'flashcard' && <FlashcardDemo onContinue={advance} />}
    </div>
  );
}
